import { createStore } from 'vuex'
import { createEntityId, getEntityPrefix } from '../helpers/id'
import { createFirebaseDataRepository } from '../services/data/firebaseDataRepository'
import { appendAuditLog } from '../services/firebase/auditLogService'
import { markRegistrationSuccess } from '../helpers/registrationThrottle'
import { notifyActionError } from '../helpers/errorMessages'
import { loginWithEmail, logout, registerWithEmail, sendPasswordReset, subscribeToAuthState } from '../services/firebase/authService'
import { ADMIN_PANEL_ROLES, createUserProfile, fetchUser } from '../services/firebase/usersService'
import seoModule from './seo'

const NOTIFICATION_TYPES = ['success', 'warning', 'error', 'info']
const COLLECTION_KEYS = ['clinics', 'services', 'products', 'articles', 'orders', 'ShopOrders', 'CallBack', 'ContactMessages']
const CATEGORY_KEYS = ['serviceCategories', 'productCategories', 'articleCategories']
const dataRepository = createFirebaseDataRepository()
let authInitPromise = null
// Firebase сам логинит только что созданный аккаунт. Пока идет регистрация,
// подписка на состояние авторизации не должна разлогинивать его раньше, чем
// будет создан документ Users/{uid}: сессией управляет сам action `register`.
let registrationInProgress = false

const COLLECTION_TITLES = {
  clinics: 'Клиника',
  services: 'Услуга',
  products: 'Товар',
  articles: 'Статья'
}

const CATEGORY_TITLES = {
  serviceCategories: 'Категории услуг',
  productCategories: 'Категории товаров',
  articleCategories: 'Категории статей'
}

const PAGE_TITLES = {
  home: 'Главная',
  about: 'О клинике',
  services: 'Услуги',
  serviceDetail: 'Детальная услуга',
  prices: 'Прайс',
  shop: 'Магазин',
  product: 'Товар',
  blog: 'Блог',
  contacts: 'Контакты'
}
function canAccessAdmin(profile) {
  return Boolean(profile && !profile.isBlocked && ADMIN_PANEL_ROLES.includes(profile.role))
}

function clone(value) {
  return JSON.parse(JSON.stringify(value))
}

function localizedValue(value) {
  if (!value) return ''
  if (typeof value === 'object' && !Array.isArray(value)) return value.ua || value.ru || value.en || value.name || value.title || ''
  return String(value)
}

function itemTitle(item) {
  if (!item) return ''
  return localizedValue(item.title || item.name || item.city || item.subject || item.id)
}

function currentRoute() {
  const hash = window.location.hash || ''
  if (hash.startsWith('#')) return hash.slice(1) || '/'
  return window.location.pathname || '/'
}
function downloadJson(filename, data) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json;charset=utf-8' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  link.click()
  URL.revokeObjectURL(link.href)
}

function hasEntityPrefix(id, type) {
  return typeof id === 'string' && id.startsWith(`${getEntityPrefix(type)}-`)
}

function normalizeIdsInList(list, type) {
  (list || []).forEach(item => {
    if (!item || hasEntityPrefix(item.id, type)) return
    item.id = createEntityId(type)
  })
}

function ensureDbShape(db) {
  if (!db.categories) db.categories = {}
  CATEGORY_KEYS.forEach(key => {
    if (!Array.isArray(db.categories[key])) db.categories[key] = []
  })

  if (!db.collections) db.collections = {}
  COLLECTION_KEYS.forEach(key => {
    if (!db.collections[key]) db.collections[key] = { items: [] }
    if (Array.isArray(db.collections[key])) db.collections[key] = { items: db.collections[key] }
    if (!Array.isArray(db.collections[key].items)) db.collections[key].items = []
  })

  if (!db.general) db.general = { sections: [] }
  if (!Array.isArray(db.general.sections)) db.general.sections = []
  if (!db.pages) db.pages = {}
}

function normalizeCategory(category) {
  if (!category) return
  if (!category.title && category.name) category.title = category.name
  if (!category.name && category.title) category.name = category.title
  if (category.order === undefined || category.order === null) category.order = 0
}

function normalizeDbEntityIds(db) {
  ensureDbShape(db)
  CATEGORY_KEYS.forEach(key => {
    normalizeIdsInList(db.categories[key], key)
    db.categories[key].forEach(normalizeCategory)
  })
  COLLECTION_KEYS.forEach(key => normalizeIdsInList(db.collections[key].items, key))
}

function createEmptyVersions() {
  return {
    categories: {},
    general: null,
    pages: null,
    collections: {}
  }
}

function createNotification(payload = {}) {
  const type = NOTIFICATION_TYPES.includes(payload.type) ? payload.type : 'success'
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type,
    title: payload.title || '',
    message: payload.message || '',
    duration: typeof payload.duration === 'number' ? payload.duration : 4200
  }
}

const UI_ACTIVITY_ACTIONS = {
  saveGeneralSection: 'Сохраняем настройки',
  createCollectionItem: 'Создаём запись',
  updateCollectionItem: 'Сохраняем изменения',
  deleteCollectionItem: 'Удаляем запись',
  saveCategoryList: 'Сохраняем категории',
  savePage: 'Сохраняем страницу',
  saveSeoGlobalSettings: 'Сохраняем SEO-настройки',
  saveSeoPageBlock: 'Сохраняем SEO страницы',
  saveSeoEntityBlock: 'Сохраняем SEO сущности',
  saveSeoScriptsList: 'Сохраняем скрипты'
}

const uiActivityPlugin = store => {
  const finish = action => {
    if (UI_ACTIVITY_ACTIONS[action.type]) store.commit('finishUiActivity')
  }

  store.subscribeAction({
    before: action => {
      const label = UI_ACTIVITY_ACTIONS[action.type]
      if (label) store.commit('startUiActivity', label)
    },
    after: finish,
    error: finish
  })
}

export default createStore({
  plugins: [uiActivityPlugin],
  modules: {
    seo: seoModule
  },
  state: {
    isLoading: false,
    activeLang: 'ua',
    db: null,
    dbVersions: createEmptyVersions(),
    error: '',
    authReady: false,
    authUser: null,
    authProfile: null,
    authError: '',
    uiActivityCount: 0,
    uiActivityLabel: '',
    isRefreshing: false,
    notifications: []
  },
  getters: {
    isLoading: state => state.isLoading,
    activeLang: state => state.activeLang,
    db: state => state.db,
    pages: state => state.db ? state.db.pages : {},
    general: state => state.db ? state.db.general : {},
    collections: state => state.db ? state.db.collections : {},
    categories: state => state.db ? state.db.categories : {},
    dbVersions: state => state.dbVersions,
    authReady: state => state.authReady,
    authUser: state => state.authUser,
    authProfile: state => state.authProfile,
    isUiActivity: state => state.uiActivityCount > 0,
    uiActivityLabel: state => state.uiActivityLabel,
    isAuthenticated: state => Boolean(state.authUser && canAccessAdmin(state.authProfile)),
    isAdmin: state => Boolean(state.authProfile && state.authProfile.role === 'Admin' && !state.authProfile.isBlocked),
    // Повторяет правило Firestore: запись в коллекцию SiteSeo разрешена только роли Admin.
    // Остальные сотрудники видят раздел SEO в режиме просмотра.
    canEditSeo: (state, getters) => getters.isAdmin,
    isRefreshing: state => state.isRefreshing,
    notifications: state => state.notifications
  },
  mutations: {
    setLoading(state, value) { state.isLoading = value },
    setError(state, value) { state.error = value },
    setLang(state, lang) { state.activeLang = lang },
    setRefreshing(state, value) { state.isRefreshing = value },
    setDb(state, payload) {
      const db = payload && payload.data ? payload.data : payload
      state.db = db
      state.dbVersions = (payload && payload.versions) || createEmptyVersions()
    },
    setAuthReady(state, value) { state.authReady = value },
    setAuthUser(state, user) {
      state.authUser = user ? { uid: user.uid, email: user.email, displayName: user.displayName || '' } : null
    },
    setAuthProfile(state, profile) { state.authProfile = profile },
    setAuthError(state, value) { state.authError = value },
    startUiActivity(state, label) {
      state.uiActivityCount += 1
      state.uiActivityLabel = label || 'Выполняем операцию'
    },
    finishUiActivity(state) {
      state.uiActivityCount = Math.max(0, state.uiActivityCount - 1)
      if (!state.uiActivityCount) state.uiActivityLabel = ''
    },
    addNotification(state, notification) { state.notifications.unshift(notification) },
    removeNotification(state, notificationId) {
      state.notifications = state.notifications.filter(item => item.id !== notificationId)
    },
    upsertCollectionItem(state, { collectionKey, item }) {
      if (!state.db.collections[collectionKey]) state.db.collections[collectionKey] = { items: [] }
      if (!state.dbVersions.collections[collectionKey]) state.dbVersions.collections[collectionKey] = {}
      const collectionItems = state.db.collections[collectionKey].items
      const nextItem = clone(item)
      const updatedAt = item.updatedAt || null
      const index = collectionItems.findIndex(existingItem => existingItem.id === nextItem.id)
      if (index >= 0) collectionItems.splice(index, 1, nextItem)
      else collectionItems.unshift(nextItem)
      state.dbVersions.collections[collectionKey][nextItem.id] = updatedAt
    },
    removeCollectionItem(state, { collectionKey, itemId }) {
      const collection = state.db.collections[collectionKey]
      if (!collection) return
      collection.items = collection.items.filter(item => item.id !== itemId)
      if (state.dbVersions.collections[collectionKey]) delete state.dbVersions.collections[collectionKey][itemId]
    },
    setCategoryList(state, { categoryKey, items, updatedAt }) {
      state.db.categories[categoryKey] = clone(items)
      state.dbVersions.categories[categoryKey] = updatedAt || null
    },
    setPage(state, { pageKey, content, updatedAt }) {
      state.db.pages[pageKey] = clone(content)
      state.dbVersions.pages = updatedAt || null
    },
    setGeneral(state, { general, updatedAt }) {
      state.db.general = clone(general)
      state.dbVersions.general = updatedAt || null
    }
  },
  actions: {
    notify({ commit }, payload) {
      const notification = createNotification(payload)
      commit('addNotification', notification)
      if (notification.duration > 0) {
        window.setTimeout(() => commit('removeNotification', notification.id), notification.duration)
      }
      return Promise.resolve(notification.id)
    },
    dismissNotification({ commit }, notificationId) {
      commit('removeNotification', notificationId)
      return Promise.resolve()
    },
    initAuth({ commit, state }) {
      if (state.authReady) return Promise.resolve(state.authUser)
      if (authInitPromise) return authInitPromise

      authInitPromise = new Promise(resolve => {
        subscribeToAuthState(async user => {
          if (user && registrationInProgress) return

          commit('setAuthUser', user)
          if (!user) {
            commit('setAuthProfile', null)
            commit('setAuthReady', true)
            resolve(null)
            return
          }

          const profile = await fetchUser(user.uid)
          if (!canAccessAdmin(profile)) {
            await logout()
            commit('setAuthUser', null)
            commit('setAuthProfile', null)
            commit('setAuthReady', true)
            resolve(null)
            return
          }

          commit('setAuthProfile', profile)
          commit('setAuthReady', true)
          resolve(user)
        })
      })

      return authInitPromise
    },
    login({ commit }, { email, password }) {
      commit('setAuthError', '')
      return loginWithEmail(email, password)
        .then(async user => {
          const profile = await fetchUser(user.uid)
          if (!canAccessAdmin(profile)) {
            await logout()
            throw new Error('У пользователя нет доступа к админке или аккаунт заблокирован.')
          }
          commit('setAuthUser', user)
          commit('setAuthProfile', profile)
          return user
        })
        .catch(error => {
          commit('setAuthError', error.message)
          return Promise.reject(error)
        })
    },
    async register({ commit }, { name, email, password }) {
      commit('setAuthError', '')
      registrationInProgress = true

      try {
        const user = await registerWithEmail(email, password, name)

        try {
          await createUserProfile({ uid: user.uid, email: user.email || email, name })
        } catch (error) {
          const profileError = new Error('Аккаунт создан, но профиль не сохранен. Обратитесь к администратору.')
          profileError.code = 'profile/create-failed'
          profileError.cause = error
          throw profileError
        } finally {
          // Новый аккаунт получает роль `User` и не имеет доступа к админке,
          // поэтому сессию сразу завершаем и возвращаем пользователя ко входу.
          await logout()
          commit('setAuthUser', null)
          commit('setAuthProfile', null)
        }

        markRegistrationSuccess()
        return user
      } catch (error) {
        commit('setAuthError', error.message)
        throw error
      } finally {
        registrationInProgress = false
      }
    },
    resetPassword({ commit }, { email }) {
      commit('setAuthError', '')
      return sendPasswordReset(email)
        .catch(error => {
          // Не подсказываем, зарегистрирован ли email: наружу отдаем успех.
          if (error && error.code === 'auth/user-not-found') return
          commit('setAuthError', error.message)
          return Promise.reject(error)
        })
    },
    logout({ commit }) {
      return logout().then(() => {
        commit('setAuthUser', null)
        commit('setAuthProfile', null)
      })
    },
    // Общая кнопка «Обновить»: живет в store, потому что вызывается и из топбара,
    // и из шапки дашборда, а состояние загрузки у них должно быть общим.
    async refreshAll({ commit, dispatch, getters }) {
      if (getters.isRefreshing || !getters.isAuthenticated) return

      // Экраны, которые держат данные вне общего db (SEO, логи, заявки),
      // подписываются на событие и добавляют свои задачи в общий Promise.all.
      const refreshTasks = []
      window.dispatchEvent(new CustomEvent('admin:refresh-data', {
        detail: { tasks: refreshTasks }
      }))

      commit('setRefreshing', true)
      try {
        await Promise.all([dispatch('fetchDb'), ...refreshTasks])
        await dispatch('notify', { type: 'success', title: 'Данные обновлены' })
      } catch (error) {
        if (!error) return
      } finally {
        commit('setRefreshing', false)
      }
    },
    fetchDb({ commit, dispatch }) {
      commit('setLoading', true)
      return dataRepository.loadDb()
        .then(result => {
          normalizeDbEntityIds(result.data)
          commit('setDb', result)
        })
        .catch(async error => {
          commit('setError', error.message)
          await notifyActionError(dispatch, error, 'Ошибка загрузки данных')
          throw error
        })
        .finally(() => commit('setLoading', false))
    },
    async saveGeneralSection({ commit, dispatch, state }, { sectionKey, content }) {
      try {
        const result = await dataRepository.saveGeneralSection(sectionKey, content, state.dbVersions.general)
        commit('setGeneral', result)
        await dispatch('notify', { type: 'success', title: 'Общая секция сохранена' })
        await dispatch('recordAuditLog', {
          action: 'save_general_settings',
          entityType: 'general',
          entityId: sectionKey,
          title: `Общие настройки: ${sectionKey}`,
          details: { sectionKey }
        })
        return result.general
      } catch (error) {
        await notifyActionError(dispatch, error, 'Общая секция не сохранена')
        throw error
      }
    },
    async createCollectionItem({ commit, dispatch }, { collectionKey, item }) {
      try {
        const savedItem = await dataRepository.createCollectionItem(collectionKey, item)
        commit('upsertCollectionItem', { collectionKey, item: savedItem })
        await dispatch('notify', { type: 'success', title: 'Запись создана' })
        await dispatch('recordAuditLog', {
          action: 'create_record',
          entityType: collectionKey,
          entityId: savedItem.id,
          title: itemTitle(savedItem) || `${COLLECTION_TITLES[collectionKey] || collectionKey} ${savedItem.id}`,
          details: { collectionKey, entityTitle: itemTitle(savedItem) }
        })
        return savedItem
      } catch (error) {
        await notifyActionError(dispatch, error, 'Запись не создана')
        throw error
      }
    },
    async updateCollectionItem({ commit, dispatch, state }, { collectionKey, item }) {
      try {
        const expectedUpdatedAt = state.dbVersions.collections[collectionKey] && state.dbVersions.collections[collectionKey][item.id]
        const savedItem = await dataRepository.updateCollectionItem(collectionKey, item, expectedUpdatedAt)
        commit('upsertCollectionItem', { collectionKey, item: savedItem })
        await dispatch('notify', { type: 'success', title: 'Изменения сохранены' })
        await dispatch('recordAuditLog', {
          action: 'update_record',
          entityType: collectionKey,
          entityId: savedItem.id,
          title: itemTitle(savedItem) || `${COLLECTION_TITLES[collectionKey] || collectionKey} ${savedItem.id}`,
          details: { collectionKey, entityTitle: itemTitle(savedItem) }
        })
        return savedItem
      } catch (error) {
        await notifyActionError(dispatch, error, 'Изменения не сохранены')
        throw error
      }
    },
    async deleteCollectionItem({ commit, dispatch, state }, { collectionKey, itemId }) {
      try {
        const expectedUpdatedAt = state.dbVersions.collections[collectionKey] && state.dbVersions.collections[collectionKey][itemId]
        const collection = state.db.collections[collectionKey]
        const deletedItem = collection && collection.items ? collection.items.find(item => item.id === itemId) : null
        await dataRepository.deleteCollectionItem(collectionKey, itemId, expectedUpdatedAt)
        commit('removeCollectionItem', { collectionKey, itemId })
        await dispatch('notify', { type: 'success', title: 'Запись удалена' })
        await dispatch('recordAuditLog', {
          action: 'delete_record',
          entityType: collectionKey,
          entityId: itemId,
          title: itemTitle(deletedItem) || `${COLLECTION_TITLES[collectionKey] || collectionKey} ${itemId}`,
          details: { collectionKey, entityTitle: itemTitle(deletedItem) }
        })
      } catch (error) {
        await notifyActionError(dispatch, error, 'Запись не удалена')
        throw error
      }
    },
    async saveCategoryList({ commit, dispatch, state }, { categoryKey, items }) {
      try {
        const previousItems = state.db.categories[categoryKey] || []
        const previousIds = new Set(previousItems.map(item => item.id).filter(Boolean))
        const nextIds = new Set((items || []).map(item => item.id).filter(Boolean))
        const removedIds = Array.from(previousIds).filter(id => !nextIds.has(id))
        const addedIds = Array.from(nextIds).filter(id => !previousIds.has(id))
        const result = await dataRepository.saveCategoryList(categoryKey, items, state.dbVersions.categories[categoryKey])
        commit('setCategoryList', { categoryKey, items: result.items, updatedAt: result.updatedAt })
        await dispatch('notify', { type: 'success', title: 'Категории сохранены' })
        await dispatch('recordAuditLog', {
          action: removedIds.length ? 'delete_category' : 'save_categories',
          entityType: 'categories',
          entityId: categoryKey,
          title: CATEGORY_TITLES[categoryKey] || categoryKey,
          details: { categoryKey, count: result.items.length, addedIds, removedIds }
        })
        return result.items
      } catch (error) {
        await notifyActionError(dispatch, error, 'Категории не сохранены')
        throw error
      }
    },
    async exportDb({ dispatch }) {
      const db = await dataRepository.exportDb()
      normalizeDbEntityIds(db)
      downloadJson('optometry_clinic_db.json', db)
      await dispatch('notify', {
        type: 'success',
        title: 'JSON-резервная копия экспортирована',
        message: 'Резервная копия сформирована из актуальных данных Firestore.'
      })
      return db
    },
    async savePage({ commit, dispatch, state }, { pageKey, content }) {
      try {
        const result = await dataRepository.savePage(pageKey, content, state.dbVersions.pages)
        commit('setPage', { pageKey, content: result.page, updatedAt: result.updatedAt })
        await dispatch('notify', { type: 'success', title: 'Страница сохранена' })
        await dispatch('recordAuditLog', {
          action: 'save_page',
          entityType: 'page',
          entityId: pageKey,
          title: PAGE_TITLES[pageKey] || pageKey,
          details: { pageKey }
        })
        return result.page
      } catch (error) {
        await notifyActionError(dispatch, error, 'Страница не сохранена')
        throw error
      }
    },
    async recordAuditLog({ dispatch, state }, payload) {
      try {
        return await appendAuditLog({
          ...payload,
          actor: {
            uid: state.authUser ? state.authUser.uid : '',
            name: state.authProfile ? state.authProfile.name : '',
            role: state.authProfile ? state.authProfile.role : ''
          },
          route: payload.route || currentRoute()
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('[Audit log]', error && error.code, error && error.message)
        await dispatch('notify', {
          type: 'warning',
          title: 'Данные сохранены без записи в журнал',
          message: 'Основные данные сохранены, но запись аудита не удалось добавить в Firebase Storage.'
        })
        return null
      }
    }
  }
})
